/**
 * `ChatRescueBanner` — the emergency-mode signal (P3 §8.7 "rescue red
 * promptbox"). A compact, hard-to-miss red pill in the composer toolbar,
 * rendered ONLY when the org's active chat profile has descended to the
 * RESCUE rung (all normal providers down) or is UNAVAILABLE. Fallback (still
 * working on a backup model) is signalled more quietly by the `ModelBadge`
 * red (i); this pill is reserved for the full emergency state.
 *
 * Source: the same `/llm-status` `effectiveTiers` the badge reads — a faithful
 * mirror of what the runtime concierge resolver is doing (the runtime swaps to
 * the emergency persona on the same `providerHealth` signal). Fail-soft: an org
 * with no alias table (no `effectiveTiers`) renders nothing.
 */

import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLlmStatus } from "@/hooks/useLlmStatus";

export function ChatRescueBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeModel, status } = useLlmStatus();

  if (!activeModel || activeModel.source !== "org-chat-profile") return null;
  const base = activeModel.chatProfile.split(":")[0];
  const eff = status?.effectiveTiers?.find((tt) => tt.alias === base);
  if (!eff || (eff.tier !== "rescue" && eff.tier !== "unavailable")) return null;

  return (
    <button
      type="button"
      data-testid="chat-rescue-banner"
      onClick={() => navigate("/settings")}
      title={t("chat.rescueBanner.tooltip")}
      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-700 transition-colors hover:bg-red-200 dark:bg-red-950/50 dark:text-red-300 dark:hover:bg-red-900/60"
    >
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      <span className="max-w-[14rem] truncate">{t("chat.rescueBanner.label")}</span>
    </button>
  );
}
